<script setup>
import { ref, watch, nextTick, computed, inject } from 'vue'
import DropdownMenu from '@/components/Dropdown/DropdownMenu.vue'
import { useAnchoredMenu } from '@/composables/useAnchoredMenu'

const props = defineProps({
  sidebarExpanded: {
    type: Boolean,
    default: false
  },
  query: {
    type: String,
    default: ''
  },
  savedSearchId: {
    type: String,
    default: null
  },
  savedSearchName: {
    type: String,
    default: ''
  },
  savedSearchDescription: {
    type: String,
    default: ''
  },
  savedSearchQuery: {
    type: String,
    default: ''
  },
  loading: {
    type: Boolean,
    default: false
  },
  showTrigger: {
    type: Boolean,
    default: true
  },
  mode: {
    type: String,
    default: 'sidebar', // 'sidebar' | 'inline'
    validator: (v) => ['sidebar', 'inline'].includes(v)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  deleteLoading: {
    type: Boolean,
    default: false
  },
  // Subscription props
  isSubscribed: {
    type: Boolean,
    default: false
  },
  subscriptionLoading: {
    type: Boolean,
    default: false
  },
  defaultName: {
    type: String,
    default: ''
  },
  defaultDescription: {
    type: String,
    default: ''
  },
  anchorElement: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['save', 'overwrite', 'delete', 'close', 'subscribe', 'unsubscribe'])

const isOpen = ref(false)
const triggerRef = ref(null)
const menuComp = ref(null)

// Expanded state comes from the enclosing ToolRail, same as ToolRailDropdown.
// Falls back to the prop for hosts that aren't a ToolRail.
const railExpanded = inject('railExpanded', null)
const effectiveExpanded = computed(() =>
  railExpanded ? railExpanded.value : props.sidebarExpanded
)

// Form fields
const name = ref('')
const description = ref('')

// Delete confirmation state
const deleteConfirming = ref(false)

// Compute save state based on query/filters
const saveState = computed(() => {
  if (!props.savedSearchId) return 'unsaved'
  if (props.query === props.savedSearchQuery) return 'saved'
  return 'diverged'
})

// Check if form fields have been modified from saved values
const hasFormChanges = computed(() => {
  if (!props.savedSearchId) return false
  return name.value.trim() !== props.savedSearchName ||
         description.value.trim() !== props.savedSearchDescription
})

// State-based icon color class
const stateColorClass = computed(() => {
  switch (saveState.value) {
    case 'saved': return 'state-saved'
    case 'diverged': return 'state-diverged'
    default: return 'state-unsaved'
  }
})

// Button label based on state
const buttonLabel = computed(() => {
  switch (saveState.value) {
    case 'saved': return 'saved'
    case 'diverged': return 'modified'
    default: return 'save'
  }
})

// Inline mode hangs a panel under the pen button; sidebar mode sits to the left
// of the rail, bottom-aligned with its trigger.
const { menuStyle, reposition } = useAnchoredMenu({
  anchorRef: () => props.anchorElement?.$el || props.anchorElement || triggerRef.value,
  menuRef: () => menuComp.value?.el,
  isOpen,
  placement: props.mode === 'inline' ? 'below' : 'left',
  align: 'end',
  width: 300,
  onDismiss: () => close()
})

function toggle() {
  if (props.disabled || props.loading || props.deleteLoading) return
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
  deleteConfirming.value = false
  emit('close')
}

function open() {
  isOpen.value = true
}

// Initialize form with saved search data when opening
watch(isOpen, async (newVal) => {
  if (newVal) {
    // Pre-fill with existing data if editing
    if (props.savedSearchId) {
      name.value = props.savedSearchName || ''
      description.value = props.savedSearchDescription || ''
    } else {
      name.value = props.defaultName || ''
      description.value = props.defaultDescription || ''
    }
    // Reset delete confirmation state
    deleteConfirming.value = false
  }
})

// Reposition when savedSearchId changes — saving reveals the notifications
// section, so the panel grows and a bottom-aligned menu has to move.
watch(() => props.savedSearchId, async (newVal, oldVal) => {
  if (isOpen.value && newVal !== oldVal) {
    await nextTick()
    reposition()
  }
})

// Handle save action (no auto-close - user may want to set notifications)
function handleSave() {
  if (!name.value.trim()) return

  emit('save', {
    name: name.value.trim(),
    description: description.value.trim()
  })
}

// Handle overwrite action (no auto-close - user may want to continue editing)
function handleOverwrite() {
  if (!name.value.trim()) return

  emit('overwrite', {
    name: name.value.trim(),
    description: description.value.trim()
  })
}

// Handle delete actions
function handleDeleteClick() {
  deleteConfirming.value = true
}

function handleDeleteCancel() {
  deleteConfirming.value = false
}

function handleDeleteConfirm() {
  emit('delete')
  // Don't close here - parent will close after successful delete
}

// Toggle subscription
function toggleSubscription() {
  if (props.isSubscribed) {
    emit('unsubscribe')
  } else {
    emit('subscribe')
  }
}

defineExpose({ close, open, isOpen })
</script>

<template>
  <component :is="showTrigger ? 'li' : 'div'" class="nav-item-wrapper save-dropdown-wrapper" :class="{ 'inline-mode': mode === 'inline' }">
    <!-- Sidebar trigger button -->
    <div
      v-if="showTrigger"
      ref="triggerRef"
      class="sidebar-nav"
      :class="[stateColorClass, { 'active': isOpen, 'loading': loading, 'disabled': disabled }]"
      @click="toggle"
    >
      <font-awesome-icon class="icon" :icon="['fas', 'save']" />
      <span class="nav-text">{{ buttonLabel }}</span>
      <div class="nav-popout" v-if="!effectiveExpanded && !isOpen">
        <span>{{ buttonLabel }}</span>
      </div>
    </div>
    <!-- Inline trigger button for edit mode -->
    <button
      v-else
      ref="triggerRef"
      class="btn-edit-trigger"
      :class="{ 'is-open': isOpen }"
      title="Edit search"
      @click="toggle"
    >
      <font-awesome-icon :icon="['fas', 'pen']" />
    </button>

    <DropdownMenu
      ref="menuComp"
      :show="isOpen"
      :header-label="mode === 'inline' ? 'Edit Search' : 'Save Search'"
      :header-subtext="mode === 'inline' ? 'Update name and description' : (saveState === 'unsaved' ? 'Save current query' : 'Update or save as new')"
      :loading="loading || deleteLoading"
      :loading-text="deleteLoading ? 'Deleting...' : 'Saving...'"
      :menu-style="menuStyle"
    >
          <!-- Form content -->
          <div class="save-dropdown-content">
            <div class="form-group">
              <label for="search-name">Name</label>
              <input
                id="search-name"
                v-model="name"
                type="text"
                placeholder="My saved search"
                maxlength="255"
                @keydown.enter.prevent="saveState === 'diverged' ? handleOverwrite() : handleSave()"
              />
            </div>

            <div class="form-group">
              <label for="search-description">Description <span class="optional">(optional)</span></label>
              <textarea
                id="search-description"
                v-model="description"
                placeholder="Describe what this search is for..."
                maxlength="1000"
                rows="2"
              ></textarea>
            </div>

            <!-- Action buttons -->
            <div class="save-dropdown-actions">
              <template v-if="saveState === 'diverged'">
                <button
                  class="btn btn-secondary"
                  :disabled="!name.trim() || loading"
                  @click="handleSave"
                >
                  <font-awesome-icon :icon="['fas', 'plus']" />
                  Save as New
                </button>
                <button
                  class="btn btn-primary"
                  :disabled="!name.trim() || loading"
                  @click="handleOverwrite"
                >
                  <font-awesome-icon :icon="['fas', 'sync']" />
                  Overwrite
                </button>
              </template>
              <template v-else-if="saveState === 'saved' && hasFormChanges">
                <button
                  class="btn btn-primary full-width"
                  :disabled="!name.trim() || loading"
                  @click="handleOverwrite"
                >
                  <font-awesome-icon :icon="['fas', 'save']" />
                  Update
                </button>
              </template>
              <template v-else-if="saveState !== 'saved'">
                <button
                  class="btn btn-primary full-width"
                  :disabled="!name.trim() || loading"
                  @click="handleSave"
                >
                  <font-awesome-icon :icon="['fas', 'save']" />
                  Save Search
                </button>
              </template>
            </div>

            <!-- Subscription section (only when search is saved, not shown in inline mode) -->
            <div v-if="savedSearchId && mode !== 'inline'" class="subscription-section">
              <span class="subscription-text">
                {{ isSubscribed ? 'Receiving notifications' : 'Get notified on new results' }}
              </span>
              <button
                class="btn-bell"
                :class="{ 'is-subscribed': isSubscribed }"
                :disabled="subscriptionLoading"
                :title="isSubscribed ? 'Unsubscribe from notifications' : 'Subscribe to notifications'"
                @click="toggleSubscription"
              >
                <font-awesome-icon
                  v-if="subscriptionLoading"
                  icon="spinner"
                  spin
                />
                <font-awesome-icon
                  v-else
                  :icon="isSubscribed ? ['fas', 'bell'] : ['far', 'bell']"
                />
              </button>
            </div>

            <!-- Delete section (only when search is saved) -->
            <div v-if="savedSearchId" class="delete-section">
              <template v-if="!deleteConfirming">
                <button
                  class="btn-delete"
                  :disabled="loading || deleteLoading"
                  @click="handleDeleteClick"
                >
                  <font-awesome-icon :icon="['fas', 'trash']" />
                  Delete search
                </button>
              </template>
              <template v-else>
                <div class="delete-confirm">
                  <span class="delete-confirm-text">Delete this search?</span>
                  <div class="delete-confirm-actions">
                    <button
                      class="btn-delete-cancel"
                      :disabled="deleteLoading"
                      @click="handleDeleteCancel"
                    >
                      Cancel
                    </button>
                    <button
                      class="btn-delete-confirm"
                      :disabled="deleteLoading"
                      @click="handleDeleteConfirm"
                    >
                      <font-awesome-icon v-if="deleteLoading" icon="spinner" spin />
                      <span v-else>Delete</span>
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>
    </DropdownMenu>
  </component>
</template>

<style scoped>
.save-dropdown-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

.sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  transition: var(--transition);
  border-radius: var(--radius-lg);
  position: relative;
  gap: 0;
  cursor: pointer;
}

.sidebar-nav.disabled {
  color: var(--text-disabled);
  cursor: not-allowed;
}

.sidebar-nav.loading {
  opacity: 0.6;
  cursor: wait;
}

.sidebar-nav.active {
  background-color: var(--surface-hover);
  color: white;
}

.sidebar-nav:not(.loading):not(.disabled):hover {
  color: white;
  background-color: var(--surface-hover);
}

.sidebar-nav.disabled:hover {
  background-color: var(--surface-page);
}

/* State colors */
.sidebar-nav.state-unsaved {
  color: var(--text-secondary);
}

.sidebar-nav.state-unsaved:not(.disabled):hover,
.sidebar-nav.state-unsaved.active {
  color: white;
}

.sidebar-nav.state-saved {
  color: var(--color-success);
}

.sidebar-nav.state-saved .icon {
  color: var(--color-success);
}

.sidebar-nav.state-diverged {
  color: var(--color-warning);
}

/* Disabled overrides state colors */
.sidebar-nav.disabled,
.sidebar-nav.disabled .icon {
  color: var(--text-disabled);
}

.sidebar-nav.state-diverged .icon {
  color: var(--color-warning);
}

.sidebar-nav .icon {
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-text {
  display: none;
}

.nav-popout {
  display: flex;
  position: absolute;
  right: calc(100% - 1.3rem);
  left: auto;
  top: 0;
  height: 100%;
  background: var(--surface-hover);
  padding: 0.75rem 1rem;
  border-radius: var(--radius-lg);
  align-items: center;
  white-space: nowrap;
  z-index: 100;
  color: white;
  font-size: 0.95rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  text-decoration: none;
}

.save-dropdown-wrapper:hover .nav-popout {
  opacity: 1;
  pointer-events: auto;
}

/* Muted popout for disabled items */
.sidebar-nav.disabled .nav-popout {
  background: var(--surface-page);
  color: var(--text-disabled);
}

.sidebar-nav.active .nav-popout {
  opacity: 0 !important;
  pointer-events: none !important;
}
</style>

<!-- Styles for expanded sidebar state -->
<style>
#tool-sidebar.expanded .save-dropdown-wrapper .sidebar-nav {
  justify-content: flex-start;
}

#tool-sidebar.expanded .save-dropdown-wrapper .nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
}

#tool-sidebar.expanded .save-dropdown-wrapper .sidebar-nav .icon {
  margin-right: 0.75rem;
}

#tool-sidebar:not(.expanded) .save-dropdown-wrapper .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}

/* Dropdown menu styles */
.save-dropdown-content {
  position: relative;
  padding: 1rem;
}

/* Form styles */
.form-group {
  margin-bottom: 0.75rem;
}

.form-group label {
  display: block;
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-bottom: 0.35rem;
}

.form-group .optional {
  color: var(--text-faint);
  font-weight: normal;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: var(--surface-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: 0.85rem;
  transition: border-color 0.15s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--accent);
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: var(--text-disabled);
}

.form-group textarea {
  resize: none;
  min-height: 50px;
}

/* Action buttons */
.save-dropdown-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.save-dropdown-actions .btn {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: var(--radius-md);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: var(--transition-fast);
}

.save-dropdown-actions .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.save-dropdown-actions .btn-primary {
  background: var(--accent);
  color: white;
}

.save-dropdown-actions .btn-primary:hover:not(:disabled) {
  background: var(--accent);
}

.save-dropdown-actions .btn-secondary {
  background: var(--surface-control);
  color: var(--text-primary);
}

.save-dropdown-actions .btn-secondary:hover:not(:disabled) {
  background: var(--surface-control);
}

.save-dropdown-actions .btn.full-width {
  flex: none;
  width: 100%;
}

/* Subscription section */
.subscription-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.subscription-text {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.btn-bell {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-overlay-2);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: var(--transition);
  flex-shrink: 0;
}

.btn-bell:hover:not(:disabled) {
  background: rgba(245, 158, 11, 0.15);
  border-color: rgba(245, 158, 11, 0.3);
  color: var(--color-warning);
}

.btn-bell.is-subscribed {
  background: rgba(245, 158, 11, 0.15);
  border-color: rgba(245, 158, 11, 0.3);
  color: var(--color-warning);
}

.btn-bell.is-subscribed:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: var(--color-error);
}

.btn-bell:disabled {
  opacity: 0.5;
  cursor: wait;
}

/* Loading overlay */
/* Inline mode styles */
.save-dropdown-wrapper.inline-mode {
  padding: 0;
  display: inline-flex;
}

/* Inline edit trigger button */
.btn-edit-trigger {
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--surface-overlay-2);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-edit-trigger:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: var(--text-primary);
}

.btn-edit-trigger.is-open {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: var(--text-primary);
}

/* Delete section */
.delete-section {
  margin-top: 1rem;
}

.btn-delete {
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: var(--radius-md);
  color: var(--text-muted);
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: var(--transition-fast);
}

.btn-delete:hover:not(:disabled) {
  background: var(--color-error-soft);
  border-color: rgba(239, 68, 68, 0.3);
  color: var(--color-error);
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Delete confirmation */
.delete-confirm {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.delete-confirm-text {
  font-size: 0.8rem;
  color: var(--color-error);
  text-align: center;
}

.delete-confirm-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-delete-cancel,
.btn-delete-confirm {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: var(--transition-fast);
}

.btn-delete-cancel {
  background: var(--surface-control);
  border: 1px solid var(--surface-control);
  color: var(--text-primary);
}

.btn-delete-cancel:hover:not(:disabled) {
  background: var(--surface-control);
}

.btn-delete-confirm {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: var(--color-error);
  min-width: 80px;
}

.btn-delete-confirm:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.25);
  border-color: rgba(239, 68, 68, 0.5);
}

.btn-delete-cancel:disabled,
.btn-delete-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
